/**
 * @fileoverview Session API Client (Contract-First Design)
 * @module api-client/clients/session
 *
 * 100% type-safe Session API client generated from contract.
 * All methods, types, and validation come from SESSION_API_CONTRACT.
 */

import {
	SESSION_API_CONTRACT,
	type CreateSessionPayload,
	type InitializeSessionResponse,
	type VerifySessionApiResponse,
	type DeleteSessionResponse,
	type CleanupSessionsResponse,
	type PromoteSessionPayload,
	type PromoteSessionResponse,
	type SessionTracking,
	type ClearPaymentFlagApiResponse,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Session API Client
 *
 * Contract-first client for session management operations.
 * All methods are type-safe and validated against the API contract.
 *
 * This client handles:
 * - Session initialization
 * - Session verification (CSRF token management)
 * - Session deletion (logout)
 * - Session promotion (visitor → user)
 * - Session cleanup (admin)
 *
 * @example
 * ```typescript
 * const sessionClient = new SessionClient(httpClient);
 *
 * // Initialize a new session
 * const session = await sessionClient.initialize({
 *   clientType: ClientType.WEB,
 *   fingerprint: 'abc123'
 * });
 *
 * // Verify current session (get CSRF token)
 * const verified = await sessionClient.verify();
 * console.log(verified.csrfToken);
 *
 * // Delete session (logout)
 * await sessionClient.delete();
 *
 * // Promote session (visitor → user)
 * await sessionClient.promote({
 *   userId: 'user-uuid',
 *   metadata: { source: 'payment' }
 * });
 * ```
 */
export class SessionClient extends ContractClient<typeof SESSION_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, SESSION_API_CONTRACT, {
			validateResponses: true, // ✅ ENABLED: Validate responses against contract
			validateRequests: true, // ✅ ENABLED: Validate requests before sending
			throwOnValidationError: true, // Throw detailed errors on validation failure
		});
	}

	/**
	 * Initialize a new session
	 * POST /session
	 *
	 * Creates a new session and returns session ID + CSRF token.
	 * This is typically called automatically by the SessionGuard.
	 */
	async initialize(payload: CreateSessionPayload): Promise<InitializeSessionResponse> {
		return this.execute('initialize', { body: payload });
	}

	/**
	 * Verify current session
	 * GET /session/verify
	 *
	 * Verifies the current session and returns session ID + CSRF token.
	 * This endpoint auto-initializes a session if one doesn't exist.
	 * Use this to get the CSRF token for subsequent requests.
	 */
	async verify(): Promise<VerifySessionApiResponse> {
		return this.execute('verify', {});
	}

	/**
	 * Delete current session (logout)
	 * DELETE /session
	 *
	 * Deletes the current session and invalidates the CSRF token.
	 * Use this for user logout.
	 */
	async delete(): Promise<DeleteSessionResponse> {
		return this.execute('delete', {});
	}

	/**
	 * Cleanup expired sessions (admin endpoint)
	 * POST /session/cleanup
	 *
	 * Marks expired sessions in logs for cleanup.
	 * This is an admin-only endpoint.
	 */
	async cleanup(): Promise<CleanupSessionsResponse> {
		return this.execute('cleanup', {});
	}

	/**
	 * Promote anonymous session to user session
	 * POST /session/promote
	 *
	 * Promotes an anonymous visitor session to a user session.
	 * This is typically called after user registration or purchase.
	 * Internal use only - called by backend services.
	 */
	async promote(payload: PromoteSessionPayload): Promise<PromoteSessionResponse> {
		return this.execute('promote', { body: payload });
	}

	/**
	 * Get session by ID (admin endpoint)
	 * GET /session/:id
	 *
	 * Retrieves a session by its ID.
	 * This is an admin-only endpoint.
	 */
	async getById(id: string): Promise<SessionTracking> {
		return this.execute('getById', { params: { id } });
	}

	/**
	 * Clear payment completion flag from session
	 * POST /session/clear-payment-flag
	 *
	 * Clears the payment completion flag from session metadata.
	 * Called after thank-you page is viewed to prevent reuse.
	 * @returns Promise<ClearPaymentFlagApiResponse> - Wrapped response: { success, data: { message }, timestamp }
	 */
	async clearPaymentFlag(): Promise<ClearPaymentFlagApiResponse> {
		return this.execute('clearPaymentFlag', {});
	}
}
